import axios from "axios";
import { API_URL } from "../constants";

/**
 * The cabinet summary — erp-backend GET /:companyId/cabinet/summary. One request
 * answers the bell number, the «Užduotys» page and the menu hints. A queue is
 * `null` when the person has nothing there (or no access to it).
 */

/** One thing the person can open from the «Užduotys» page. */
export interface TaskItem {
  id: string;
  /** asset_act | work_safety | leave | fuel | trip | fuel_report | user_document | other */
  kind: string;
  title: string;
  companyId: string;
  companyName: string;
  /** When the task appeared (ISO). */
  since: string;
  dueDate: string | null;
  signType: "Click" | "Dokobit" | null;
  /** The FE address that opens the task today. */
  link: string;
}

export interface TaskList {
  count: number;
  /** A preview, oldest first; `count` is the whole number. */
  items: TaskItem[];
}

export type MedicalState = "ok" | "upload_needed" | "waiting" | "none";

export interface CabinetSummary {
  /** The one number: the bell and the «Užduotys» menu badge. */
  count: number;
  personal: {
    sign: TaskList;
    training: TaskList;
    pinned: TaskList;
    tripReport: TaskList;
    /** The previous month's missing fuel reports, one item per vehicle. */
    fuelReport: TaskList;
    medical: { state: MedicalState; validUntil: string | null };
    expiring: TaskList;
    notInApp: { count: number };
  };
  queues: {
    /** `items`: the documents one by one, sent only while there are at most 5 of them. */
    signWork: { count: number; click: number; dokobit: number; oldestDays: number; items?: TaskItem[] } | null;
    paperWork: { count: number } | null;
    team: { count: number } | null;
    probation: { count: number } | null;
    invoices: { count: number; oldestDays: number | null } | null;
    ws: { count: number } | null;
    customerFinance: { count: number } | null;
    medicalQueue: { count: number } | null;
    assetRequests: { count: number } | null;
    review: { count: number; oldestDate: string } | null;
    sent: { count: number } | null;
  };
  waitingOthers: TaskList;
  capabilities: { hasFuel: boolean };
}

export async function fetchCabinetSummary(companyId: string): Promise<CabinetSummary> {
  const res = await axios.get(`${API_URL}/${companyId}/cabinet/summary`);
  return res.data;
}

/**
 * What the server says about one month of one vehicle of the person (see
 * `GET /:companyId/cabinet/fuel-months`). The first rule that applies, in this order:
 * no report (`missing`, or `current` for a month not over yet), no live main document
 * (`draft`), annulled, declined by a signer (`rejected`), fully signed (`approved`),
 * the person's own signature pending (`to_sign`), otherwise the manager's (`waiting_manager`).
 */
export type FuelMonthStatus =
  | "missing"
  | "current"
  | "draft"
  | "to_sign"
  | "waiting_manager"
  | "rejected"
  | "approved"
  | "annulled";

export interface FuelMonthRow {
  /** YYYY-MM */
  period: string;
  asset: { id: string; name: string; plate: string | null };
  status: FuelMonthStatus;
  /** Set whenever a report exists. */
  reportId: string | null;
}

/** The person's fuel reporting, newest month first: the current month and the 11 before it. */
export async function fetchCabinetFuelMonths(companyId: string): Promise<FuelMonthRow[]> {
  const res = await axios.get(`${API_URL}/${companyId}/cabinet/fuel-months`);
  return res.data.rows;
}
